export interface GlobalSearchPayload {
  pageIndex: number;
  pageSize: number;
  searchTerm: string;
  searchCriterion: string;
  startDate: string | null;
  endDate: string | null;
}

/** Normalize date values to ISO-8601 for POST /api/Search/search. */
export function formatGlobalSearchDateParam(
  value: string | Date | null | undefined,
  boundary: 'start' | 'end' = 'start'
): string | null {
  if (value == null || value === '') {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    const [year, month, day] = value.trim().split('-').map(Number);
    const normalized = new Date(year, month - 1, day);
    if (boundary === 'end') {
      normalized.setHours(23, 59, 59, 999);
    } else {
      normalized.setHours(0, 0, 0, 0);
    }
    return normalized.toISOString();
  }

  if (boundary === 'end') {
    const end = new Date(date);
    if (end.getHours() === 0 && end.getMinutes() === 0 && end.getSeconds() === 0 && end.getMilliseconds() === 0) {
      end.setHours(23, 59, 59, 999);
    }
    return end.toISOString();
  }

  return date.toISOString();
}

/** Build request body for POST /api/Search/search. */
export function buildGlobalSearchApiPayload(input: {
  pageIndex: number;
  pageSize: number;
  searchTerm: string;
  searchCriterion: string;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
}): GlobalSearchPayload {
  return {
    pageIndex: input.pageIndex,
    pageSize: input.pageSize,
    searchTerm: (input.searchTerm || '').trim(),
    searchCriterion: (input.searchCriterion || 'all').trim() || 'all',
    startDate: formatGlobalSearchDateParam(input.startDate, 'start'),
    endDate: formatGlobalSearchDateParam(input.endDate, 'end'),
  };
}

export function mapSearchCriterionToBackend(value: string | null | undefined): string {
  const searchCriterionMap: Record<string, string> = {
    all: 'all',
    '': 'all',
    fileName: 'filename',
    eventName: 'eventname',
    eventDuration: 'eventduration',
    tagName: 'tag',
    folderName: 'foldername',
    locationName: 'locationname',
    extension: 'extension',
    fileType: 'filetype',
  };
  return searchCriterionMap[String(value ?? '').trim()] ?? 'all';
}

export const GLOBAL_SEARCH_FILE_TYPES = ['pdf', 'word', 'image', 'video'] as const;

export type GlobalSearchFileType = (typeof GLOBAL_SEARCH_FILE_TYPES)[number];

export function isGlobalSearchFileType(value: string): value is GlobalSearchFileType {
  return (GLOBAL_SEARCH_FILE_TYPES as readonly string[]).includes(value);
}

export function filterGlobalSearchFileTypes(values: string[] | null | undefined): GlobalSearchFileType[] {
  return (values ?? []).filter(isGlobalSearchFileType);
}

export type GlobalSearchActiveCriterion =
  | ''
  | 'fileName'
  | 'folderName'
  | 'tagName'
  | 'locationName'
  | 'extension'
  | 'fileType'
  | 'dateRange';

export type GlobalSearchMetadataCriterion = '' | 'fileName' | 'folderName' | 'tagName' | 'extension';

export type GlobalSearchDatePreset = '' | 'last7' | 'last30' | 'thisYear' | 'custom';

export function resolveGlobalSearchDateRange(input: {
  datePreset?: string | null;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
}): { startDate: string | null; endDate: string | null } {
  const preset = String(input.datePreset ?? '').trim();
  if (!preset || preset === 'any') {
    return { startDate: null, endDate: null };
  }

  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

  switch (preset) {
    case 'last7': {
      const start = startOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6));
      return { startDate: start.toISOString(), endDate: endOfDay(now).toISOString() };
    }
    case 'last30': {
      const start = startOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29));
      return { startDate: start.toISOString(), endDate: endOfDay(now).toISOString() };
    }
    case 'thisYear': {
      const start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
      return { startDate: start.toISOString(), endDate: endOfDay(now).toISOString() };
    }
    case 'custom':
      return {
        startDate: formatGlobalSearchDateParam(input.startDate, 'start'),
        endDate: formatGlobalSearchDateParam(input.endDate, 'end'),
      };
    default:
      return { startDate: null, endDate: null };
  }
}

export function buildGlobalSearchFilterState(input: {
  metadataCriterion?: string | null;
  searchTerm?: string;
  fileTypes?: string[] | null;
  datePreset?: string | null;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  /** @deprecated Prefer metadataCriterion + fileTypes + datePreset */
  activeCriterion?: GlobalSearchActiveCriterion | string;
  /** @deprecated Prefer fileTypes */
  fileType?: string | null;
}): Pick<GlobalSearchPayload, 'searchTerm' | 'searchCriterion' | 'startDate' | 'endDate'> {
  if (input.metadataCriterion !== undefined || input.fileTypes !== undefined || input.datePreset !== undefined) {
    const trimmedTerm = (input.searchTerm || '').trim();
    const types = filterGlobalSearchFileTypes(input.fileTypes);
    const metadata = mapSearchCriterionToBackend(input.metadataCriterion);

    let searchCriterion = metadata !== 'all' ? metadata : (types.length > 0 ? 'filetype' : 'all');
    let searchTerm = trimmedTerm;

    if (!searchTerm && searchCriterion === 'filetype' && types.length > 0) {
      searchTerm = types.join(',');
    }

    const dates = resolveGlobalSearchDateRange({
      datePreset: input.datePreset,
      startDate: input.startDate,
      endDate: input.endDate,
    });

    return {
      searchTerm,
      searchCriterion,
      startDate: dates.startDate,
      endDate: dates.endDate,
    };
  }

  const trimmedTerm = (input.searchTerm || '').trim();
  const criterion = String(input.activeCriterion || '').trim() as GlobalSearchActiveCriterion;

  switch (criterion) {
    case 'fileName':
    case 'folderName':
    case 'tagName':
    case 'locationName':
    case 'extension':
      return {
        searchTerm: trimmedTerm,
        searchCriterion: mapSearchCriterionToBackend(criterion),
        startDate: null,
        endDate: null,
      };
    case 'fileType': {
      const fileType = String(input.fileType || '').trim();
      return {
        searchTerm: trimmedTerm || fileType,
        searchCriterion: 'filetype',
        startDate: null,
        endDate: null,
      };
    }
    case 'dateRange':
      return {
        searchTerm: trimmedTerm,
        searchCriterion: 'all',
        startDate: formatGlobalSearchDateParam(input.startDate, 'start'),
        endDate: formatGlobalSearchDateParam(input.endDate, 'end'),
      };
    default:
      return {
        searchTerm: trimmedTerm,
        searchCriterion: 'all',
        startDate: null,
        endDate: null,
      };
  }
}

export function resolveGlobalSearchApiCriterion(
  searchType: string | null | undefined,
  fileTypes: string[] | null | undefined
): string {
  const mapped = mapSearchCriterionToBackend(searchType);
  if (mapped !== 'all') {
    return mapped;
  }
  if (filterGlobalSearchFileTypes(fileTypes).length > 0) {
    return 'filetype';
  }
  return 'all';
}

export function resolveGlobalSearchApiTerm(
  searchTerm: string,
  searchType: string | null | undefined,
  fileTypes: string[] | null | undefined
): string {
  const trimmed = (searchTerm || '').trim();
  if (trimmed) {
    return trimmed;
  }
  const normalizedTypes = filterGlobalSearchFileTypes(fileTypes);
  if (
    normalizedTypes.length > 0 &&
    (mapSearchCriterionToBackend(searchType) === 'filetype' || !searchType)
  ) {
    return normalizedTypes.join(',');
  }
  return '';
}

export interface GlobalSearchPreviewResult {
  id: number;
  entityType: string;
  folderId: number | null;
  title: string;
  subtitle: string;
  thumbnail: string;
  fileName: string;
  tags: string[];
  isShared: boolean;
  raw: any;
}

export function extractGlobalSearchItems(response: any): any[] {
  const data = response?.data ?? response?.Data ?? response;
  if (Array.isArray(data?.items)) {
    return data.items;
  }
  if (Array.isArray(data?.Items)) {
    return data.Items;
  }
  if (Array.isArray(data)) {
    return data;
  }
  return [];
}

export function extractGlobalSearchTotalCount(response: any, fallback = 0): number {
  const data = response?.data ?? response?.Data ?? response;
  const total = Number(
    data?.totalCount ??
      data?.TotalCount ??
      data?.total ??
      data?.Total ??
      fallback
  );
  return Number.isFinite(total) ? total : fallback;
}

export function normalizeGlobalSearchEntityType(item: any): string {
  const raw = String(item?.entityType ?? item?.EntityType ?? '').trim().toLowerCase();
  if (raw === 'folder') {
    return 'Folder';
  }
  if (raw === 'document') {
    return 'Document';
  }
  return 'File';
}

function formatGlobalSearchBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  let index = 0;
  let value = bytes;
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }
  return `${value.toFixed(index === 0 ? 0 : 2)} ${units[index]}`;
}

function getGlobalSearchExtension(name: string): string {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx + 1) : '';
}

export function resolveGlobalSearchLibraryLabel(item: any, scopeLabel?: string): string {
  if (scopeLabel) {
    return scopeLabel;
  }
  if (item?.isShared === true || item?.IsShared === true) {
    return 'Shared';
  }
  const libraryType = Number(item?.libraryType ?? item?.LibraryType ?? 0);
  if (libraryType === 2) {
    return 'Org Assets';
  }
  if (libraryType === 1) {
    return 'My Files';
  }
  return 'Library';
}

export function buildGlobalSearchPreviewSubtitle(
  item: any,
  entityType: string,
  libraryLabel: string
): string {
  const typeLabel =
    entityType === 'Folder' ? 'Folder' : entityType === 'Document' ? 'Document' : 'File';
  const parts = [typeLabel, libraryLabel];

  if (entityType === 'Folder') {
    const count = item?.itemCount ?? item?.ItemCount ?? item?.fileCount ?? item?.FileCount;
    if (count != null && Number.isFinite(Number(count))) {
      parts.push(`${Number(count)} items`);
    }
  } else {
    const size = item?.fileSize ?? item?.FileSize ?? item?.size ?? item?.Size;
    if (size != null && Number.isFinite(Number(size))) {
      const formatted = formatGlobalSearchBytes(Number(size));
      if (formatted) {
        parts.push(formatted);
      }
    } else {
      const ext = getGlobalSearchExtension(String(item?.fileName ?? item?.FileName ?? ''));
      if (ext) {
        parts.push(ext.toUpperCase());
      }
    }
  }

  return parts.filter(Boolean).join(' · ');
}

export function mapGlobalSearchPreviewResult(
  item: any,
  options?: { libraryLabel?: string }
): GlobalSearchPreviewResult {
  const entityType = normalizeGlobalSearchEntityType(item);
  const id = Number(item?.entityId ?? item?.EntityId ?? item?.id ?? 0);
  const fileName = String(item?.fileName ?? item?.FileName ?? item?.entityName ?? item?.EntityName ?? '');
  const title = String(item?.entityName ?? item?.EntityName ?? fileName);
  const libraryLabel = resolveGlobalSearchLibraryLabel(item, options?.libraryLabel);

  return {
    id,
    entityType,
    folderId: item?.folderId ?? item?.FolderId ?? null,
    title,
    subtitle: buildGlobalSearchPreviewSubtitle(item, entityType, libraryLabel),
    thumbnail:
      item?.filePreview ??
      item?.FilePreview ??
      item?.thumbnailUrl ??
      item?.ThumbnailUrl ??
      '',
    fileName,
    tags: (item?.tags ?? item?.Tags ?? [])
      .map((tag: any) => tag?.tag ?? tag?.Tag ?? tag)
      .filter(Boolean),
    isShared: item?.isShared === true || item?.IsShared === true,
    raw: item,
  };
}
